/**
 * Quiz Questions Screen
 * View and manage questions and sections for a specific quiz
 * 
 * Features:
 * - Create/edit/delete sections
 * - Assign questions to sections
 * - Reorder questions with drag handles
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ActivityIndicator,
  TextInput,
  Modal,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams, useFocusEffect } from "expo-router";
import { useThemeColors, useTheme } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { QuizQuestion, QuizCategory } from "@/modules/quiz";

/**
 * Section interface
 */
interface QuizSection {
  id: string;
  quiz_id: string;
  name: string;
  description: string | null;
  order_index: number;
  show_ranking_after: boolean;
  lottie_animation: string | null;
  created_at: string;
}

/**
 * Difficulty badge colors
 */
const DIFFICULTY_COLORS: Record<string, string> = {
  easy: "#10B981",
  medium: "#F59E0B",
  hard: "#EF4444",
};

/**
 * Question type labels
 */
const QUESTION_TYPE_LABELS: Record<string, string> = {
  multiple_choice: "Meerkeuze",
  true_false: "Waar/Onwaar",
  text_answer: "Open vraag",
};

/**
 * Question type icons
 */
const QUESTION_TYPE_ICONS: Record<string, string> = {
  multiple_choice: "list",
  true_false: "swap-horizontal",
  text_answer: "text",
};

interface Quiz {
  id: string;
  name: string;
  icon: string;
  color: string;
  question_count: number;
}

export default function QuizQuestionsScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const { isDark } = useTheme();

  // State
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [sections, setSections] = useState<QuizSection[]>([]);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isReordering, setIsReordering] = useState(false);
  
  // Section modal state
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<QuizSection | null>(null);
  const [sectionName, setSectionName] = useState("");
  const [sectionDescription, setSectionDescription] = useState("");
  const [showRankingAfter, setShowRankingAfter] = useState(true);
  const [isSavingSection, setIsSavingSection] = useState(false);

  // Assign to section modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [targetSectionId, setTargetSectionId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  /**
   * Fetch quiz, sections and questions
   */
  const fetchData = useCallback(async () => {
    try {
      // Fetch quiz
      const { data: quizData, error: quizError } = await supabase
        .from("quizzes")
        .select("id, name, icon, color, question_count")
        .eq("id", id)
        .single();

      if (quizError) {
        Alert.alert("Fout", "Kon quiz niet laden");
        router.back();
        return;
      }

      setQuiz(quizData);

      // Fetch sections
      const { data: sectionsData, error: sectionsError } = await supabase
        .from("quiz_sections")
        .select("*")
        .eq("quiz_id", id)
        .order("order_index", { ascending: true });

      if (!sectionsError && sectionsData) {
        setSections(sectionsData);
      }

      // Fetch questions
      const { data: questionsData, error: questionsError } = await supabase
        .from("quiz_questions")
        .select(`
          *,
          category:quiz_categories(*)
        `)
        .eq("quiz_id", id)
        .order("order_index", { ascending: true });

      if (questionsError) {
        console.error("Error fetching questions:", questionsError);
      } else {
        setQuestions(questionsData as QuizQuestion[]);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, router]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  }, [fetchData]);

  /**
   * Open section modal for create/edit
   */
  const handleOpenSectionModal = (section?: QuizSection) => {
    if (section) {
      setEditingSection(section);
      setSectionName(section.name);
      setSectionDescription(section.description || "");
      setShowRankingAfter(section.show_ranking_after);
    } else {
      setEditingSection(null);
      setSectionName("");
      setSectionDescription("");
      setShowRankingAfter(true);
    }
    setShowSectionModal(true);
  };

  /**
   * Save section
   */
  const handleSaveSection = async () => {
    if (!sectionName.trim()) {
      Alert.alert("Fout", "Vul een naam in voor de sectie");
      return;
    }

    setIsSavingSection(true);

    try {
      if (editingSection) {
        // Update existing section
        const { error } = await supabase
          .from("quiz_sections")
          .update({
            name: sectionName.trim(),
            description: sectionDescription.trim() || null,
            show_ranking_after: showRankingAfter,
          })
          .eq("id", editingSection.id);

        if (error) throw error;
      } else {
        // Create new section
        const newOrderIndex = sections.length;
        const { error } = await supabase
          .from("quiz_sections")
          .insert({
            quiz_id: id,
            name: sectionName.trim(),
            description: sectionDescription.trim() || null,
            order_index: newOrderIndex,
            show_ranking_after: showRankingAfter,
          });

        if (error) throw error;
      }

      setShowSectionModal(false);
      await fetchData();
    } catch (error) {
      console.error("Error saving section:", error);
      Alert.alert("Fout", "Kon sectie niet opslaan");
    } finally {
      setIsSavingSection(false);
    }
  };

  /**
   * Delete section
   */
  const handleDeleteSection = (section: QuizSection) => {
    const questionsInSection = questions.filter(q => q.section_id === section.id);
    
    Alert.alert(
      "Sectie Verwijderen",
      questionsInSection.length > 0
        ? `Deze sectie bevat ${questionsInSection.length} vragen. De vragen worden niet verwijderd maar hebben dan geen sectie meer.`
        : `Weet je zeker dat je "${section.name}" wilt verwijderen?`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            // First, unlink questions from this section
            if (questionsInSection.length > 0) {
              await supabase
                .from("quiz_questions")
                .update({ section_id: null })
                .eq("section_id", section.id);
            }

            // Then delete the section
            const { error } = await supabase
              .from("quiz_sections")
              .delete()
              .eq("id", section.id);

            if (error) {
              Alert.alert("Fout", "Kon sectie niet verwijderen");
              return;
            }

            await fetchData();
          },
        },
      ]
    );
  };

  /**
   * Open assign to section modal
   */
  const handleOpenAssignModal = () => {
    setSelectedQuestions(new Set());
    setTargetSectionId(null);
    setShowAssignModal(true);
  };

  /**
   * Toggle question selection for assignment
   */
  const toggleQuestionSelection = (questionId: string) => {
    setSelectedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  /**
   * Assign selected questions to target section
   */
  const handleAssignToSection = async () => {
    if (selectedQuestions.size === 0) {
      Alert.alert("Fout", "Selecteer minstens 1 vraag");
      return;
    }

    setIsAssigning(true);

    try {
      const { error } = await supabase
        .from("quiz_questions")
        .update({ section_id: targetSectionId })
        .in("id", Array.from(selectedQuestions));

      if (error) throw error;

      setShowAssignModal(false);
      await fetchData();
    } catch (error) {
      console.error("Error assigning questions:", error);
      Alert.alert("Fout", "Kon vragen niet toewijzen");
    } finally {
      setIsAssigning(false);
    }
  };

  /**
   * Move question up in order
   */
  const handleMoveQuestionUp = async (question: QuizQuestion, questionsInGroup: QuizQuestion[]) => {
    const currentIndex = questionsInGroup.findIndex(q => q.id === question.id);
    if (currentIndex <= 0) return;

    const prevQuestion = questionsInGroup[currentIndex - 1];
    
    try {
      // Swap order_index values
      await Promise.all([
        supabase
          .from("quiz_questions")
          .update({ order_index: prevQuestion.order_index })
          .eq("id", question.id),
        supabase
          .from("quiz_questions")
          .update({ order_index: question.order_index })
          .eq("id", prevQuestion.id),
      ]);

      await fetchData();
    } catch (error) {
      console.error("Error moving question:", error);
    }
  };

  /**
   * Move question down in order
   */
  const handleMoveQuestionDown = async (question: QuizQuestion, questionsInGroup: QuizQuestion[]) => {
    const currentIndex = questionsInGroup.findIndex(q => q.id === question.id);
    if (currentIndex >= questionsInGroup.length - 1) return;

    const nextQuestion = questionsInGroup[currentIndex + 1];
    
    try {
      // Swap order_index values
      await Promise.all([
        supabase
          .from("quiz_questions")
          .update({ order_index: nextQuestion.order_index })
          .eq("id", question.id),
        supabase
          .from("quiz_questions")
          .update({ order_index: question.order_index })
          .eq("id", nextQuestion.id),
      ]);

      await fetchData();
    } catch (error) {
      console.error("Error moving question:", error);
    }
  };

  /**
   * Delete question
   */
  const handleDeleteQuestion = (question: QuizQuestion) => {
    Alert.alert(
      "Vraag Verwijderen",
      `Weet je zeker dat je deze vraag wilt verwijderen?`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            const { error } = await supabase
              .from("quiz_questions")
              .delete()
              .eq("id", question.id);

            if (error) {
              Alert.alert("Fout", "Kon vraag niet verwijderen");
              return;
            }

            setQuestions((prev) => prev.filter((q) => q.id !== question.id));
          },
        },
      ]
    );
  };

  /**
   * Toggle question active
   */
  const handleToggleActive = async (question: QuizQuestion) => {
    const { error } = await supabase
      .from("quiz_questions")
      .update({ is_active: !question.is_active })
      .eq("id", question.id);

    if (error) {
      Alert.alert("Fout", "Kon vraag niet bijwerken");
      return;
    }

    setQuestions((prev) =>
      prev.map((q) =>
        q.id === question.id ? { ...q, is_active: !q.is_active } : q
      )
    );
  };

  // Filter by search
  const filteredQuestions = questions.filter((q) =>
    q.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group questions by section
  const getQuestionsForSection = (sectionId: string | null) => {
    return filteredQuestions
      .filter(q => q.section_id === sectionId)
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  };

  // Questions without a section
  const unsectionedQuestions = getQuestionsForSection(null);

  // Auto-refresh when screen gains focus (after adding/editing question)
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Progress calculation
  const currentCount = questions.length;
  const activeCount = questions.filter(q => q.is_active).length;

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
      {/* Header */}
      <View className="px-4 py-4 flex-row items-center justify-between">
        <View className="flex-row items-center flex-1">
          <TouchableOpacity
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={22} color={colors.text} />
          </TouchableOpacity>

          {quiz && (
            <View className="flex-1">
              <View className="flex-row items-center">
                <View
                  className="w-8 h-8 rounded-lg items-center justify-center mr-2"
                  style={{ backgroundColor: `${quiz.color}20` }}
                >
                  <Ionicons name={quiz.icon as any} size={16} color={quiz.color} />
                </View>
                <Text
                  className="text-lg font-bold flex-1"
                  style={{ color: colors.text }}
                  numberOfLines={1}
                >
                  {quiz.name}
                </Text>
              </View>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                {currentCount} vragen • {sections.length} secties
              </Text>
            </View>
          )}
        </View>

        <View className="flex-row items-center">
          {/* Reorder toggle */}
          <TouchableOpacity
            onPress={() => setIsReordering(!isReordering)}
            className="w-10 h-10 rounded-full items-center justify-center mr-2"
            style={{ 
              backgroundColor: isReordering ? quiz?.color || colors.primary : colors.surface,
            }}
            accessibilityRole="button"
            accessibilityLabel={isReordering ? "Klaar met sorteren" : "Volgorde aanpassen"}
          >
            <Ionicons 
              name={isReordering ? "checkmark" : "swap-vertical"} 
              size={20} 
              color={isReordering ? "#FFFFFF" : colors.text} 
            />
          </TouchableOpacity>

          {/* Settings */}
          <TouchableOpacity
            onPress={() => router.push(`/(admin)/games/quiz/${id}`)}
            className="w-10 h-10 rounded-full items-center justify-center mr-2"
            style={{ backgroundColor: colors.surface }}
            accessibilityRole="button"
            accessibilityLabel="Quiz instellingen"
          >
            <Ionicons name="settings-outline" size={20} color={colors.text} />
          </TouchableOpacity>

          {/* Add Question */}
          <TouchableOpacity
            onPress={() => router.push(`/(admin)/games/quiz/${id}/add-question`)}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: quiz?.color || colors.primary }}
            accessibilityRole="button"
            accessibilityLabel="Vraag toevoegen"
          >
            <Ionicons name="add" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Reordering Mode Banner */}
      {isReordering && (
        <View
          className="mx-4 mb-3 rounded-xl p-3 flex-row items-center"
          style={{ backgroundColor: `${quiz?.color || colors.primary}20` }}
        >
          <Ionicons name="swap-vertical" size={18} color={quiz?.color || colors.primary} />
          <Text className="ml-2 flex-1" style={{ color: quiz?.color || colors.primary }}>
            Gebruik de pijlen om vragen te verplaatsen
          </Text>
          <TouchableOpacity onPress={() => setIsReordering(false)}>
            <Text className="font-bold" style={{ color: quiz?.color || colors.primary }}>
              Klaar
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Sections & Assignment Actions */}
      <View className="px-4 mb-3 flex-row">
        <TouchableOpacity
          onPress={() => handleOpenSectionModal()}
          className="flex-1 mr-2 rounded-xl p-3 flex-row items-center justify-center"
          style={{
            backgroundColor: isDark ? "#1E3A5F" : "#EFF6FF",
            borderWidth: 1,
            borderColor: isDark ? "#3B82F6" : "#BFDBFE",
          }}
        >
          <Ionicons name="layers-outline" size={18} color="#3B82F6" />
          <Text className="ml-2 font-medium" style={{ color: "#3B82F6" }}>
            + Sectie
          </Text>
        </TouchableOpacity>

        {unsectionedQuestions.length > 0 && sections.length > 0 && (
          <TouchableOpacity
            onPress={handleOpenAssignModal}
            className="flex-1 ml-2 rounded-xl p-3 flex-row items-center justify-center"
            style={{
              backgroundColor: isDark ? "#3D2F1E" : "#FEF3C7",
              borderWidth: 1,
              borderColor: isDark ? "#F59E0B" : "#FCD34D",
            }}
          >
            <Ionicons name="arrow-forward" size={18} color="#F59E0B" />
            <Text className="ml-2 font-medium" style={{ color: "#F59E0B" }}>
              Toewijzen
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Search */}
      <View className="px-4 mb-3">
        <View
          className="flex-row items-center rounded-xl px-4 py-3"
          style={{ backgroundColor: colors.surface }}
        >
          <Ionicons name="search" size={20} color={colors.textTertiary} />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Zoek vragen..."
            placeholderTextColor={colors.textTertiary}
            className="flex-1 ml-3"
            style={{ color: colors.text }}
            accessibilityLabel="Zoek vragen"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery("")}>
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Sections with their questions */}
        {sections.map((section, index) => {
          const sectionQuestions = getQuestionsForSection(section.id);
          
          return (
            <View key={section.id} className="px-4 mb-4">
              {/* Section Header */}
              <View
                className="rounded-xl p-4 mb-2"
                style={{
                  backgroundColor: `${quiz?.color || colors.primary}15`,
                  borderWidth: 2,
                  borderColor: `${quiz?.color || colors.primary}30`,
                }}
              >
                <View className="flex-row items-center justify-between">
                  <View className="flex-1">
                    <View className="flex-row items-center">
                      <View
                        className="w-7 h-7 rounded-full items-center justify-center mr-2"
                        style={{ backgroundColor: quiz?.color || colors.primary }}
                      >
                        <Text className="text-white font-bold text-sm">{index + 1}</Text>
                      </View>
                      <Text className="font-bold text-base" style={{ color: colors.text }}>
                        {section.name}
                      </Text>
                    </View>
                    {section.description && (
                      <Text className="text-sm mt-1 ml-9" style={{ color: colors.textSecondary }}>
                        {section.description}
                      </Text>
                    )}
                    <View className="flex-row items-center mt-2 ml-9">
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        {sectionQuestions.length} vragen
                      </Text>
                      {section.show_ranking_after && (
                        <View 
                          className="flex-row items-center ml-3 px-2 py-0.5 rounded"
                          style={{ backgroundColor: "#F59E0B20" }}
                        >
                          <Ionicons name="trophy" size={12} color="#F59E0B" />
                          <Text className="text-xs ml-1" style={{ color: "#F59E0B" }}>
                            Ranking
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                  
                  <View className="flex-row items-center">
                    <TouchableOpacity
                      onPress={() => handleOpenSectionModal(section)}
                      className="w-8 h-8 rounded-full items-center justify-center mr-1"
                      style={{ backgroundColor: colors.surface }}
                      accessibilityLabel="Bewerk sectie"
                    >
                      <Ionicons name="pencil" size={14} color={colors.textSecondary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => handleDeleteSection(section)}
                      className="w-8 h-8 rounded-full items-center justify-center"
                      style={{ backgroundColor: colors.error + "20" }}
                      accessibilityLabel="Verwijder sectie"
                    >
                      <Ionicons name="trash" size={14} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Questions in this section */}
              {sectionQuestions.length === 0 ? (
                <View
                  className="rounded-xl p-4 items-center"
                  style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
                >
                  <Text style={{ color: colors.textSecondary }}>
                    Geen vragen in deze sectie
                  </Text>
                  <TouchableOpacity
                    onPress={() => router.push(`/(admin)/games/quiz/${id}/add-question?sectionId=${section.id}`)}
                    className="mt-2 px-4 py-2 rounded-lg"
                    style={{ backgroundColor: quiz?.color || colors.primary }}
                  >
                    <Text className="text-white font-medium">+ Vraag toevoegen</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                sectionQuestions.map((question, qIndex) => (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    quizColor={quiz?.color || colors.primary}
                    colors={colors}
                    isReordering={isReordering}
                    canMoveUp={qIndex > 0}
                    canMoveDown={qIndex < sectionQuestions.length - 1}
                    onMoveUp={() => handleMoveQuestionUp(question, sectionQuestions)}
                    onMoveDown={() => handleMoveQuestionDown(question, sectionQuestions)}
                    onPress={() =>
                      router.push(
                        `/(admin)/games/quiz/${id}/question/${question.id}`
                      )
                    }
                    onToggleActive={() => handleToggleActive(question)}
                    onDelete={() => handleDeleteQuestion(question)}
                  />
                ))
              )}
            </View>
          );
        })}

        {/* Unsectioned Questions */}
        {unsectionedQuestions.length > 0 && (
          <View className="px-4 mb-4">
            <View className="flex-row items-center justify-between mb-2">
              <View className="flex-row items-center">
                <Ionicons name="help-circle-outline" size={18} color={colors.textSecondary} />
                <Text className="ml-2 font-semibold" style={{ color: colors.textSecondary }}>
                  Vragen zonder sectie ({unsectionedQuestions.length})
                </Text>
              </View>
              {sections.length > 0 && (
                <TouchableOpacity
                  onPress={handleOpenAssignModal}
                  className="px-3 py-1 rounded-lg"
                  style={{ backgroundColor: "#F59E0B20" }}
                >
                  <Text className="text-sm font-medium" style={{ color: "#F59E0B" }}>
                    Toewijzen
                  </Text>
                </TouchableOpacity>
              )}
            </View>
            {unsectionedQuestions.map((question, qIndex) => (
              <QuestionCard
                key={question.id}
                question={question}
                quizColor={quiz?.color || colors.primary}
                colors={colors}
                isReordering={isReordering}
                canMoveUp={qIndex > 0}
                canMoveDown={qIndex < unsectionedQuestions.length - 1}
                onMoveUp={() => handleMoveQuestionUp(question, unsectionedQuestions)}
                onMoveDown={() => handleMoveQuestionDown(question, unsectionedQuestions)}
                onPress={() =>
                  router.push(
                    `/(admin)/games/quiz/${id}/question/${question.id}`
                  )
                }
                onToggleActive={() => handleToggleActive(question)}
                onDelete={() => handleDeleteQuestion(question)}
              />
            ))}
          </View>
        )}

        {/* Empty State */}
        {filteredQuestions.length === 0 && sections.length === 0 && (
          <View className="py-12 items-center px-4">
            <View
              className="w-16 h-16 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: `${quiz?.color || colors.primary}20` }}
            >
              <Ionicons
                name="help-circle"
                size={32}
                color={quiz?.color || colors.primary}
              />
            </View>
            <Text className="text-lg font-semibold mb-2" style={{ color: colors.text }}>
              {searchQuery ? "Geen resultaten" : "Begin met je quiz"}
            </Text>
            <Text className="text-center mb-6" style={{ color: colors.textSecondary }}>
              {searchQuery
                ? "Probeer een andere zoekterm"
                : "Voeg secties en vragen toe aan je quiz"}
            </Text>
            {!searchQuery && (
              <View className="flex-row">
                <TouchableOpacity
                  onPress={() => handleOpenSectionModal()}
                  className="px-5 py-3 rounded-xl flex-row items-center mr-2"
                  style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
                >
                  <Ionicons name="layers" size={18} color={colors.text} />
                  <Text className="ml-2 font-semibold" style={{ color: colors.text }}>Sectie</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => router.push(`/(admin)/games/quiz/${id}/add-question`)}
                  className="px-5 py-3 rounded-xl flex-row items-center"
                  style={{ backgroundColor: quiz?.color || colors.primary }}
                >
                  <Ionicons name="add" size={20} color="#FFFFFF" />
                  <Text className="ml-2 font-bold text-white">Vraag</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

        <View className="h-24" />
      </ScrollView>

      {/* Floating Add Button */}
      {(filteredQuestions.length > 0 || sections.length > 0) && !isReordering && (
        <TouchableOpacity
          onPress={() => router.push(`/(admin)/games/quiz/${id}/add-question`)}
          className="absolute bottom-6 right-6 w-14 h-14 rounded-full items-center justify-center shadow-lg"
          style={{
            backgroundColor: quiz?.color || colors.primary,
            shadowColor: quiz?.color || colors.primary,
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }}
          accessibilityRole="button"
          accessibilityLabel="Vraag toevoegen"
        >
          <Ionicons name="add" size={28} color="#FFFFFF" />
        </TouchableOpacity>
      )}

      {/* Section Modal */}
      <Modal
        visible={showSectionModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSectionModal(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <View
            className="w-full rounded-2xl p-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-6">
              <Text style={{ color: colors.text }} className="text-xl font-bold">
                {editingSection ? "Sectie Bewerken" : "Nieuwe Sectie"}
              </Text>
              <TouchableOpacity onPress={() => setShowSectionModal(false)}>
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Section Name */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Naam *
              </Text>
              <TextInput
                value={sectionName}
                onChangeText={setSectionName}
                placeholder="Bijv. Ronde 1: Muziek"
                placeholderTextColor={colors.textTertiary}
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.background,
                  color: colors.text,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              />
            </View>

            {/* Section Description */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Beschrijving (optioneel)
              </Text>
              <TextInput
                value={sectionDescription}
                onChangeText={setSectionDescription}
                placeholder="Korte beschrijving van deze ronde"
                placeholderTextColor={colors.textTertiary}
                multiline
                numberOfLines={2}
                className="rounded-xl px-4 py-3"
                style={{
                  backgroundColor: colors.background,
                  color: colors.text,
                  borderWidth: 1,
                  borderColor: colors.border,
                  minHeight: 60,
                  textAlignVertical: "top",
                }}
              />
            </View>

            {/* Show Ranking Toggle */}
            <TouchableOpacity
              onPress={() => setShowRankingAfter(!showRankingAfter)}
              className="flex-row items-center justify-between p-4 rounded-xl mb-6"
              style={{
                backgroundColor: showRankingAfter ? "#F59E0B15" : colors.background,
                borderWidth: 1,
                borderColor: showRankingAfter ? "#F59E0B" : colors.border,
              }}
            >
              <View className="flex-row items-center flex-1">
                <Ionicons 
                  name="trophy" 
                  size={20} 
                  color={showRankingAfter ? "#F59E0B" : colors.textSecondary} 
                />
                <View className="ml-3 flex-1">
                  <Text style={{ color: showRankingAfter ? "#F59E0B" : colors.text }}>
                    Toon ranking na deze sectie
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    Laat tussenstand zien op het grote scherm
                  </Text>
                </View>
              </View>
              <View
                className="w-12 h-6 rounded-full justify-center px-1"
                style={{ backgroundColor: showRankingAfter ? "#F59E0B" : colors.border }}
              >
                <View
                  className="w-5 h-5 rounded-full bg-white"
                  style={{ alignSelf: showRankingAfter ? "flex-end" : "flex-start" }}
                />
              </View>
            </TouchableOpacity>

            {/* Action Buttons */}
            <View className="flex-row gap-3">
              <TouchableOpacity
                onPress={() => setShowSectionModal(false)}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.text }} className="font-semibold">
                  Annuleren
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleSaveSection}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: quiz?.color || colors.primary }}
                disabled={isSavingSection}
              >
                {isSavingSection ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <Text className="text-white font-semibold">
                    {editingSection ? "Opslaan" : "Toevoegen"}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Assign to Section Modal */}
      <Modal
        visible={showAssignModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowAssignModal(false)}
      >
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "bottom"]}>
          {/* Header */}
          <View
            className="px-4 py-4 flex-row items-center justify-between border-b"
            style={{ borderColor: colors.border }}
          >
            <TouchableOpacity onPress={() => setShowAssignModal(false)}>
              <Text style={{ color: colors.primary }}>Annuleren</Text>
            </TouchableOpacity>
            <Text className="font-bold text-lg" style={{ color: colors.text }}>
              Vragen Toewijzen
            </Text>
            <TouchableOpacity
              onPress={handleAssignToSection}
              disabled={isAssigning || selectedQuestions.size === 0}
            >
              {isAssigning ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Text
                  style={{
                    color: selectedQuestions.size > 0 ? colors.primary : colors.textTertiary,
                    fontWeight: "bold",
                  }}
                >
                  Toewijzen ({selectedQuestions.size})
                </Text>
              )}
            </TouchableOpacity>
          </View>

          <ScrollView className="flex-1 px-4 pt-4">
            {/* Target Section Selection */}
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Toewijzen aan sectie
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <TouchableOpacity
                  onPress={() => setTargetSectionId(null)}
                  className="px-4 py-2 rounded-full mr-2"
                  style={{
                    backgroundColor: targetSectionId === null ? "#EF4444" : colors.surface,
                    borderWidth: targetSectionId === null ? 0 : 1,
                    borderColor: colors.border,
                  }}
                >
                  <Text style={{ color: targetSectionId === null ? "#FFF" : colors.text }}>
                    Geen sectie
                  </Text>
                </TouchableOpacity>
                {sections.map((s, index) => (
                  <TouchableOpacity
                    key={s.id}
                    onPress={() => setTargetSectionId(s.id)}
                    className="px-4 py-2 rounded-full mr-2 flex-row items-center"
                    style={{
                      backgroundColor: targetSectionId === s.id ? quiz?.color || colors.primary : colors.surface,
                      borderWidth: targetSectionId === s.id ? 0 : 1,
                      borderColor: colors.border,
                    }}
                  >
                    <View
                      className="w-5 h-5 rounded-full items-center justify-center mr-2"
                      style={{ backgroundColor: targetSectionId === s.id ? "rgba(255,255,255,0.3)" : colors.surfaceSecondary }}
                    >
                      <Text className="text-xs font-bold" style={{ color: targetSectionId === s.id ? "#FFF" : colors.textSecondary }}>
                        {index + 1}
                      </Text>
                    </View>
                    <Text style={{ color: targetSectionId === s.id ? "#FFF" : colors.text }}>
                      {s.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Questions to Select */}
            <View className="mb-4">
              <View className="flex-row items-center justify-between mb-2">
                <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                  Selecteer vragen
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    if (selectedQuestions.size === unsectionedQuestions.length) {
                      setSelectedQuestions(new Set());
                    } else {
                      setSelectedQuestions(new Set(unsectionedQuestions.map(q => q.id)));
                    }
                  }}
                >
                  <Text style={{ color: colors.primary }}>
                    {selectedQuestions.size === unsectionedQuestions.length ? "Deselecteer alles" : "Selecteer alles"}
                  </Text>
                </TouchableOpacity>
              </View>

              {unsectionedQuestions.map((question) => (
                <TouchableOpacity
                  key={question.id}
                  onPress={() => toggleQuestionSelection(question.id)}
                  className="flex-row items-center p-3 rounded-xl mb-2"
                  style={{
                    backgroundColor: selectedQuestions.has(question.id)
                      ? `${quiz?.color || colors.primary}15`
                      : colors.surface,
                    borderWidth: 2,
                    borderColor: selectedQuestions.has(question.id)
                      ? quiz?.color || colors.primary
                      : colors.border,
                  }}
                >
                  <View
                    className="w-6 h-6 rounded-full items-center justify-center mr-3"
                    style={{
                      backgroundColor: selectedQuestions.has(question.id)
                        ? quiz?.color || colors.primary
                        : colors.surfaceSecondary,
                    }}
                  >
                    {selectedQuestions.has(question.id) && (
                      <Ionicons name="checkmark" size={14} color="#FFFFFF" />
                    )}
                  </View>
                  <Text
                    className="flex-1"
                    style={{ color: colors.text }}
                    numberOfLines={2}
                  >
                    {question.question}
                  </Text>
                </TouchableOpacity>
              ))}

              {unsectionedQuestions.length === 0 && (
                <View className="py-8 items-center">
                  <Text style={{ color: colors.textSecondary }}>
                    Alle vragen zijn al toegewezen aan secties
                  </Text>
                </View>
              )}
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

/**
 * Question Card
 */
interface QuestionCardProps {
  question: QuizQuestion;
  quizColor: string;
  colors: ReturnType<typeof useThemeColors>;
  isReordering: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onPress: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

function QuestionCard({
  question,
  quizColor,
  colors,
  isReordering,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onPress,
  onToggleActive,
  onDelete,
}: QuestionCardProps) {
  const category = question.category as QuizCategory | undefined;
  const typeIcon = QUESTION_TYPE_ICONS[question.question_type] || "help-circle";
  const typeLabel = QUESTION_TYPE_LABELS[question.question_type] || question.question_type;

  return (
    <TouchableOpacity
      onPress={isReordering ? undefined : onPress}
      activeOpacity={isReordering ? 1 : 0.7}
      className="rounded-xl p-4 mb-2"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: question.is_active ? colors.border : colors.error + "30",
        opacity: question.is_active ? 1 : 0.6,
      }}
    >
      <View className="flex-row items-start">
        {/* Reorder Controls */}
        {isReordering && (
          <View className="mr-3 items-center justify-center">
            <TouchableOpacity
              onPress={onMoveUp}
              disabled={!canMoveUp}
              className="w-8 h-8 rounded-lg items-center justify-center mb-1"
              style={{
                backgroundColor: canMoveUp ? quizColor + "20" : colors.surfaceSecondary,
              }}
            >
              <Ionicons
                name="chevron-up"
                size={18}
                color={canMoveUp ? quizColor : colors.textTertiary}
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onMoveDown}
              disabled={!canMoveDown}
              className="w-8 h-8 rounded-lg items-center justify-center"
              style={{
                backgroundColor: canMoveDown ? quizColor + "20" : colors.surfaceSecondary,
              }}
            >
              <Ionicons
                name="chevron-down"
                size={18}
                color={canMoveDown ? quizColor : colors.textTertiary}
              />
            </TouchableOpacity>
          </View>
        )}

        {/* Type Icon (only when not reordering) */}
        {!isReordering && (
          <View
            className="w-10 h-10 rounded-lg items-center justify-center mr-3"
            style={{ backgroundColor: `${quizColor}20` }}
          >
            <Ionicons name={typeIcon as any} size={20} color={quizColor} />
          </View>
        )}

        {/* Question Text */}
        <View className="flex-1">
          <Text
            className="font-medium mb-2"
            style={{ color: colors.text }}
            numberOfLines={2}
          >
            {question.question}
          </Text>

          {/* Badges */}
          {!isReordering && (
            <View className="flex-row flex-wrap">
              {/* Type Badge */}
              <View
                className="px-2 py-0.5 rounded mr-2 mb-1 flex-row items-center"
                style={{ backgroundColor: `${quizColor}15` }}
              >
                <Ionicons name={typeIcon as any} size={10} color={quizColor} />
                <Text className="text-xs ml-1" style={{ color: quizColor }}>
                  {typeLabel}
                </Text>
              </View>

              {/* Difficulty */}
              <View
                className="px-2 py-0.5 rounded mr-2 mb-1"
                style={{ backgroundColor: `${DIFFICULTY_COLORS[question.difficulty]}20` }}
              >
                <Text
                  className="text-xs font-medium"
                  style={{ color: DIFFICULTY_COLORS[question.difficulty] }}
                >
                  {question.difficulty === "easy"
                    ? "Makkelijk"
                    : question.difficulty === "medium"
                      ? "Gemiddeld"
                      : "Moeilijk"}
                </Text>
              </View>

              {/* Category */}
              {category && (
                <View
                  className="px-2 py-0.5 rounded mb-1 flex-row items-center"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <Ionicons name={category.icon as any} size={10} color={category.color} />
                  <Text className="text-xs ml-1" style={{ color: category.color }}>
                    {category.name}
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Actions (only when not reordering) */}
        {!isReordering && (
          <View className="flex-row items-center ml-2">
            <TouchableOpacity
              onPress={onToggleActive}
              className="w-8 h-8 rounded-full items-center justify-center mr-1"
              style={{
                backgroundColor: question.is_active ? "#10B98120" : colors.error + "20",
              }}
            >
              <Ionicons
                name={question.is_active ? "eye" : "eye-off"}
                size={14}
                color={question.is_active ? "#10B981" : colors.error}
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onDelete}
              className="w-8 h-8 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.error + "20" }}
            >
              <Ionicons name="trash" size={14} color={colors.error} />
            </TouchableOpacity>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}
